/**
 * Badge — a small pill label for status / counts / tags.
 *
 * Tone drives a translucent fill + matching border/text in the same idiom
 * the diagnostic panels use for their status chips (e.g. the "DUP" /
 * "N duplicates" pills), pulled onto theme tokens.
 */
import React, { type ReactNode } from 'react';
import {
  COLORS,
  SPACE,
  RADII,
  FONT_SIZE,
  FONT_WEIGHT,
} from '../theme';

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';
export type BadgeSize = 'sm' | 'md';

export interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  size?: BadgeSize;
  /** Escape-hatch overrides merged last (e.g. minWidth, opacity, alignment). */
  style?: React.CSSProperties;
}

function toneColor(tone: BadgeTone): string {
  switch (tone) {
    case 'success': return COLORS.success;
    case 'warning': return COLORS.warning;
    case 'danger': return COLORS.danger;
    case 'info': return COLORS.info;
    case 'neutral': return COLORS.textMuted;
  }
}

/**
 * Translucent fill from a base colour. Hex tones get the `${color}22`
 * (~13% alpha) diagnostic-chip convention; rgba()-based tones (neutral
 * resolves to an rgba() token) fall back to a flat faint surface so we
 * never emit invalid CSS like `rgba(...)22`.
 */
function fillFor(color: string): string {
  return color.startsWith('#') ? `${color}22` : COLORS.surfaceHover;
}

function sizeStyle(size: BadgeSize): React.CSSProperties {
  return size === 'md'
    ? { padding: `${SPACE.xxs}px ${SPACE.md}px`, fontSize: FONT_SIZE.sm }
    : { padding: `${SPACE.xxs}px ${SPACE.sm}px`, fontSize: FONT_SIZE.xs };
}

export function Badge({ children, tone = 'neutral', size = 'sm', style }: BadgeProps): React.ReactElement {
  const color = toneColor(tone);
  const badgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: SPACE.xs,
    background: fillFor(color),
    border: `1px solid ${color}`,
    color,
    borderRadius: RADII.pill,
    fontWeight: FONT_WEIGHT.semibold,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    ...sizeStyle(size),
    ...style,
  };

  return <span style={badgeStyle}>{children}</span>;
}
